import { supabase } from '../lib/supabase';
import { Competition, Sport, SportsCategory, AgeGroup, Result } from '../types/models';

export const getAgeGroups = async (): Promise<AgeGroup[]> => {
  const { data, error } = await supabase
    .from('age_groups')
    .select('*')
    .order('birth_years', { ascending: false });
    
  if (error) {
    throw new Error(error.message);
  }
  
  return data || [];
};

export const getSportsCategories = async (): Promise<SportsCategory[]> => {
  const { data, error } = await supabase
    .from('sports_categories')
    .select('*')
    .order('category_name');
    
  if (error) {
    throw new Error(error.message);
  }
  
  return data || [];
};

export const getCategoryById = async (id: string): Promise<SportsCategory | null> => {
  const { data, error } = await supabase
    .from('sports_categories')
    .select('*')
    .eq('category_id', id)
    .single();
    
  if (error) {
    throw new Error(error.message);
  }
  
  return data;
};

export const createCategory = async (category: Omit<SportsCategory, 'category_id' | 'created_at'>): Promise<SportsCategory> => {
  const { data, error } = await supabase
    .from('sports_categories')
    .insert([category])
    .select()
    .single();
    
  if (error) {
    throw new Error(error.message);
  }
  
  return data;
};

export const updateCategory = async (id: string, category: Partial<Omit<SportsCategory, 'category_id' | 'created_at'>>): Promise<SportsCategory> => {
  const { data, error } = await supabase
    .from('sports_categories')
    .update(category)
    .eq('category_id', id)
    .select()
    .single();
    
  if (error) {
    throw new Error(error.message);
  }
  
  return data;
};

export const deleteCategory = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('sports_categories')
    .delete()
    .eq('category_id', id);
    
  if (error) {
    throw new Error(error.message);
  }
};

export const getCompetitions = async (): Promise<Competition[]> => {
  const { data, error } = await supabase
    .from('events')
    .select(`
      *,
      category:sports_categories(*)
    `)
    .order('created_at', { ascending: false });
    
  if (error) {
    throw new Error(error.message);
  }
  
  return data || [];
};

export const getCompetitionById = async (id: string): Promise<Competition | null> => {
  const { data, error } = await supabase
    .from('events')
    .select(`
      *,
      category:sports_categories(*),
      age_group:age_groups(*)
    `)
    .eq('event_id', id)
    .maybeSingle();
    
  if (error) {
    throw new Error(error.message);
  }
  
  if (!data) {
    throw new Error('Competition not found');
  }
  
  return data;
};

export const getCompetitionResults = async (eventId: string): Promise<Result[]> => {
  const { data, error } = await supabase
    .from('competition_results')
    .select(`
      *,
      student:students(
        *,
        house:houses(*)
      )
    `)
    .eq('event_id', eventId)
    .order('position', { ascending: true, nullsLast: true });

  if (error) {
    throw new Error(error.message);
  }

  return data || [];
};

export const createCompetition =async (competition: Omit<Competition, 'event_id' | 'created_at' | 'category' | 'status'>): Promise<Competition> => {
  // Check if user has admin or manager role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error('Failed to verify permissions');
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles.role_name === 'admin' || ur.roles.role_name === 'manager'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Only administrators and managers can create competitions');
  }

  // For team events with "all age groups", we'll use a special approach
  const isAllAgeGroups = competition.is_team_event && competition.age_group_id === 'all';
  
  let minBirthYear = 0;
  let maxBirthYear = 0;
  
  if (isAllAgeGroups) {
    // For "all age groups", get the min and max birth years across all age groups
    const { data: allAgeGroups, error: ageGroupsError } = await supabase
      .from('age_groups')
      .select('birth_years');
    
    if (ageGroupsError) {
      throw new Error('Failed to fetch age groups data');
    }
    
    if (!allAgeGroups || allAgeGroups.length === 0) {
      throw new Error('No age groups found');
    }
    
    // Flatten all birth years and find min/max
    const allBirthYears = allAgeGroups.flatMap(ag => ag.birth_years);
    minBirthYear = Math.min(...allBirthYears);
    maxBirthYear = Math.max(...allBirthYears);
    
    // For "all age groups", we'll set age_group_id to null in the database
    competition = { ...competition, age_group_id: null };
  } else if (competition.age_group_id) {
    // Normal case - get the age group to determine birth years
    const { data: ageGroup, error: ageGroupError } = await supabase
      .from('age_groups')
      .select('birth_years')
      .eq('age_group_id', competition.age_group_id)
      .single();

    if (ageGroupError) {
      throw new Error('Failed to fetch age group data');
    }

    if (!ageGroup || !ageGroup.birth_years.length) {
      throw new Error('Invalid age group data');
    }

    // Calculate min and max birth years from the age group
    minBirthYear = Math.min(...ageGroup.birth_years);
    maxBirthYear = Math.max(...ageGroup.birth_years);
  } else {
    throw new Error('Age group is required');
  }

  const { data, error } = await supabase
    .from('events')
    .insert([{ 
      ...competition, 
      status: 'not_started',
      min_birth_year: minBirthYear,
      max_birth_year: maxBirthYear
    }])
    .select(`
      *,
      category:sports_categories(*),
      age_group:age_groups(*)
    `)
    .single();
    
  if (error) {
    throw new Error(error.message);
  }
  
  return data;
};

export const updateCompetition = async (id: string, competition: Partial<Omit<Competition, 'event_id' | 'created_at' | 'category'>>): Promise<Competition> => {
  // Check if user has admin or manager role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error('Failed to verify permissions');
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles.role_name === 'admin' || ur.roles.role_name === 'manager'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Only administrators and managers can update competitions');
  }

  // First verify the competition exists
  const { data: existingCompetition, error: checkError } = await supabase
    .from('events')
    .select()
    .eq('event_id', id)
    .maybeSingle();
    
  if (checkError) {
    throw new Error('Error checking competition existence');
  }
  
  if (!existingCompetition) {
    throw new Error('Competition not found');
  }

  let updateData = { ...competition };

  // For team events with "all age groups"
  const isAllAgeGroups = 
    (competition.is_team_event !== undefined && competition.is_team_event) && 
    competition.age_group_id === 'all';
  
  if (isAllAgeGroups) {
    // Get the min and max birth years across all age groups
    const { data: allAgeGroups, error: ageGroupsError } = await supabase
      .from('age_groups')
      .select('birth_years');
    
    if (ageGroupsError) {
      throw new Error('Failed to fetch age groups data');
    }
    
    if (!allAgeGroups || allAgeGroups.length === 0) {
      throw new Error('No age groups found');
    }
    
    // Flatten all birth years and find min/max
    const allBirthYears = allAgeGroups.flatMap(ag => ag.birth_years);
    
    updateData = {
      ...updateData,
      age_group_id: null, // Set to null for "all age groups"
      min_birth_year: Math.min(...allBirthYears),
      max_birth_year: Math.max(...allBirthYears)
    };
  }
  // If age_group_id is being updated or is required
  else if (competition.age_group_id || !existingCompetition.age_group_id) {
    const ageGroupId = competition.age_group_id || existingCompetition.age_group_id;
    
    if (!ageGroupId) {
      throw new Error('Age group is required');
    }

    const { data: ageGroup, error: ageGroupError } = await supabase
      .from('age_groups')
      .select('birth_years')
      .eq('age_group_id', ageGroupId)
      .single();

    if (ageGroupError) {
      throw new Error('Failed to fetch age group data');
    }

    if (!ageGroup || !ageGroup.birth_years.length) {
      throw new Error('Invalid age group data');
    }

    updateData = {
      ...updateData,
      min_birth_year: Math.min(...ageGroup.birth_years),
      max_birth_year: Math.max(...ageGroup.birth_years)
    };
  }

  const { data, error } = await supabase
    .from('events')
    .update(updateData)
    .eq('event_id', id)
    .select(`
      *,
      category:sports_categories(*),
      age_group:age_groups(*)
    `)
    .maybeSingle();
    
  if (error) {
    throw new Error(error.message);
  }
  
  if (!data) {
    throw new Error('Competition not found or could not be updated');
  }
  
  return data;
};

export const updateCompetitionStatus = async (id: string, status: Competition['status']): Promise<Competition | null> => {
  // Check if user has admin or manager role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error('Failed to verify permissions');
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles.role_name === 'admin' || ur.roles.role_name === 'manager'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Only administrators and managers can update competition status');
  }

  // First verify the competition exists
  const { data: existingCompetition, error: checkError } = await supabase
    .from('events')
    .select()
    .eq('event_id', id)
    .maybeSingle();
    
  if (checkError) {
    throw new Error('Error checking competition existence');
  }
  
  if (!existingCompetition) {
    throw new Error('Competition not found');
  }

  // Then update the status
  const { data, error } = await supabase
    .from('events')
    .update({ status })
    .eq('event_id', id)
    .select()
    .maybeSingle();
    
  if (error) {
    throw new Error(error.message);
  }
  
  if (!data) {
    throw new Error('Competition not found or could not be updated');
  }
  
  return data;
};

export const deleteCompetition = async (id: string): Promise<void> => {
  // Check if user has admin role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error('Failed to verify permissions');
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles.role_name === 'admin'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Only administrators can delete competitions');
  }

  // Start a transaction to delete all related records
  const { error: deleteRegistrationsError } = await supabase
    .from('student_event_registrations')
    .delete()
    .eq('event_id', id);

  if (deleteRegistrationsError) {
    throw new Error('Error deleting competition registrations');
  }

  // Delete referee assignments
  const { error: deleteRefereesError } = await supabase
    .from('event_referees')
    .delete()
    .eq('event_id', id);

  if (deleteRefereesError) {
    throw new Error('Error deleting referee assignments');
  }

  // Delete results
  const { error: deleteResultsError } = await supabase
    .from('competition_results')
    .delete()
    .eq('event_id', id);

  if (deleteResultsError) {
    throw new Error('Error deleting competition results');
  }

  // Finally delete the competition
  const { error } = await supabase
    .from('events')
    .delete()
    .eq('event_id', id);
    
  if (error) {
    throw new Error(error.message);
  }
};

export const checkUserCanManageCompetitions = async (): Promise<boolean> => {
  const { data: userRoles } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  return userRoles?.some(ur => 
    ur.roles.role_name === 'admin' || ur.roles.role_name === 'manager'
  ) ?? false;
};

export const checkUserCanManageResults = async (): Promise<boolean> => {
  const { data: userRoles } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  return userRoles?.some(ur => 
    ur.roles.role_name === 'admin' || 
    ur.roles.role_name === 'manager' || 
    ur.roles.role_name === 'referee'
  ) ?? false;
};